import { z } from 'zod/v4';

import { endpoint } from '../http/endpoint';
import {
    AddPartsToAclRequestDTORuntype,
    AddPartsToAclResponseDTORuntype,
    GetAclPartsResponseDTORuntype,
    GetAclPartsSearchFilterDTORuntype,
    GetAuthorizedPartsDetailsResponseDTORuntype,
    GetAuthorizedPartsResponseDTORuntype,
    GetPcnUpdatesRequestRuntype,
    GetPcnUpdatesResponseRuntype,
    QuotaGatedRawQueryRuntype,
    RemovePartFromAclQueryDTORuntype,
    RemovePartFromAclResponseDTORuntype,
    SiliconExpertCredentialsInputDTORuntype,
    SiliconExpertQuotaInformationDTORuntype,
    SiliconExpertResponseBodyDTORuntype,
} from './siliconExpertBackendTypes';

export const siliconExpertEndpoints = {
    'GET /3rdparty/siliconexpert/credentials': endpoint({
        description: 'Returns credentials for SiliconExpert integration',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: SiliconExpertResponseBodyDTORuntype,
    }),
    'POST /3rdparty/siliconexpert/credentials': endpoint({
        description: 'Uploads credentials for SiliconExpert API integration',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: SiliconExpertCredentialsInputDTORuntype,
        responseBody: SiliconExpertResponseBodyDTORuntype,
        invalidates: ['GET /3rdparty/siliconexpert/credentials', 'GET /3rdparty/siliconexpert/quota'],
    }),
    'PATCH /3rdparty/siliconexpert/credentials': endpoint({
        description: 'Updates credentials for SiliconExpert API integration',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: SiliconExpertCredentialsInputDTORuntype,
        responseBody: SiliconExpertResponseBodyDTORuntype,
        invalidates: ['GET /3rdparty/siliconexpert/credentials', 'GET /3rdparty/siliconexpert/quota'],
    }),
    'DELETE /3rdparty/siliconexpert/credentials': endpoint({
        description: 'Deletes existing credentials for SiliconExpert API integration',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ deleted: z.number() }),
        invalidates: ['GET /3rdparty/siliconexpert/credentials', 'GET /3rdparty/siliconexpert/quota'],
    }),
    'POST /3rdparty/siliconexpert/enrich': endpoint({
        description: 'Requests additional part data enrichment via SiliconExpert',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            part_ids: z.array(z.string()),
        }),
        responseBody: z.object({
            search_id: z.string(),
        }),
        invalidates: [
            'POST /parts/off-the-shelf/bulk',
            'GET /3rdparty/siliconexpert/quota',
            'GET /siliconexpert/acl',
            'GET /siliconexpert/authorized-parts',
            'GET /siliconexpert/authorized-parts/details',
        ],
    }),
    'GET /3rdparty/siliconexpert/quota': endpoint({
        description: 'Returns the quota information for SiliconExpert API integration',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: SiliconExpertQuotaInformationDTORuntype,
    }),
    'POST /siliconexpert/acl': endpoint({
        description: 'Adds off-the-shelf parts to the SiliconExpert ACL',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: AddPartsToAclRequestDTORuntype,
        responseBody: AddPartsToAclResponseDTORuntype,
        invalidates: ['GET /siliconexpert/acl'],
    }),
    'GET /siliconexpert/acl': endpoint({
        description: 'Returns ACL parts from SiliconExpert',
        pathParams: z.undefined(),
        queryParams: GetAclPartsSearchFilterDTORuntype,
        requestBody: z.undefined(),
        responseBody: GetAclPartsResponseDTORuntype,
    }),
    'DELETE /siliconexpert/acl': endpoint({
        description: 'Removes part from SiliconExpert ACL',
        pathParams: z.undefined(),
        queryParams: RemovePartFromAclQueryDTORuntype,
        requestBody: z.undefined(),
        responseBody: RemovePartFromAclResponseDTORuntype,
        invalidates: ['GET /siliconexpert/acl'],
    }),

    'GET /siliconexpert/authorized-parts': endpoint({
        description: 'Returns the part ids along with the com id that have been authorized for quota usage',
        pathParams: z.undefined(),
        queryParams: z.object({ part_ids: z.uuid().array() }).optional(),
        requestBody: z.undefined(),
        responseBody: GetAuthorizedPartsResponseDTORuntype,
    }),

    'GET /siliconexpert/authorized-parts/details': endpoint({
        description:
            'Returns every SE quota-authorized part with who authorized it, when, and whether the authorization has expired',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: GetAuthorizedPartsDetailsResponseDTORuntype,
    }),

    'GET /siliconexpert/pcn': endpoint({
        description: 'Returns PCN (Product Change Notification) records from SiliconExpert',
        pathParams: z.undefined(),
        queryParams: GetPcnUpdatesRequestRuntype,
        requestBody: z.undefined(),
        responseBody: GetPcnUpdatesResponseRuntype,
    }),

    'GET /siliconexpert/raw': endpoint({
        description: 'Returns the raw SiliconExpert response for a quota-authorized part',
        pathParams: z.undefined(),
        queryParams: QuotaGatedRawQueryRuntype,
        requestBody: z.undefined(),
        responseBody: z.unknown(),
    }),

    'POST /siliconexpert/quota-estimate': endpoint({
        description: 'Estimates how many parts would consume SiliconExpert quota if enriched',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            part_ids: z.array(z.string()),
        }),
        responseBody: z.object({
            parts_consuming_quota: z.number(),
            total_parts: z.number(),
        }),
    }),
};
